/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { Country } from 'react-phone-number-input';
import { Input, PhoneNumberInput, Select, TextBox } from '../../index';
import useValidator from '../../../hooks/useValidator';
import { MiscFormActions } from '../../../pages/Misc/Misc';
import countries from '../../../utils/countries';
import { Supplier } from '../../../pages/Suppliers/Suppliers';

export type SupplierFormDataType = {
  supplierId?: string;
  businessName: string;
  contactPersonName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword?: string;
  address: string;
  otherAddress: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  notes: string;
  role: string;
};

type SupplierProps = {
  supplier: Supplier | undefined;
};

const SupplierForm = React.forwardRef<MiscFormActions, SupplierProps>(
  ({ supplier }, ref) => {
    const [account, setAccount] = React.useState({
      businessName: supplier?.businessName || '',
      contactPersonName: supplier?.contactPersonName || '',
      email: supplier?.email || '',
      phoneNumber: supplier?.phoneNumber || '',
      password: '',
      confirmPassword: '',
      address: supplier?.address || '',
      otherAddress: supplier?.otherAddress || '',
      city: supplier?.city || '',
      state: supplier?.state || '',
      postalCode: supplier?.postalCode || '',
      countryCode: supplier?.countryCode || 'GH',
      notes: supplier?.notes || '',
      role: 'Supplier',
    });
    const [errors, setErrors] = React.useState({
      businessName: false,
      contactPersonName: false,
      email: false,
      phoneNumber: false,
      password: false,
      confirmPassword: false,
    });
    const [validator, showValidationMessage] = useValidator({});

    React.useImperativeHandle(ref, () => {
      return {
        getFormData: supplierFormData,
      };
    });

    const handleAccountInputChange = (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setAccount(fndAccount => ({
        ...fndAccount,
        [name]: value,
      }));
    };

    const supplierFormData = (): SupplierFormDataType | null => {
      if (!validator.allValid()) {
        setErrors(errs => ({
          ...errs,
          businessName: !validator.fieldValid('businessName'),
          contactPersonName: !validator.fieldValid('contactPersonName'),
          email: !validator.fieldValid('email'),
          phoneNumber: !validator.fieldValid('phoneNumber'),
          password: !validator.fieldValid('password'),
          confirmPassword: !validator.fieldValid('confirmPassword'),
        }));
        showValidationMessage(true);
        return null;
      }

      const fndAccount = {
        ...account,
        ...(supplier &&
          supplier?.supplierId && { supplierId: supplier.supplierId }),
      };

      return fndAccount;
    };

    return (
      <div className="h-full bg-white">
        <form className="w-full h-full flex flex-row space-x-10 items-start">
          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b 
        border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Details</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="businessName"
                    className="text-xbase block mb-1"
                  >
                    Business Name{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    id="businessName"
                    name="businessName"
                    value={account.businessName}
                    onChange={handleAccountInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        businessName: !validator.check(
                          account.businessName,
                          'required|min:3',
                        ),
                      }));
                    }}
                    error={errors.businessName}
                  />
                  {validator.message(
                    'businessName',
                    account.businessName,
                    'required|min:3',
                    {
                      messages: {
                        required: 'Business name is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>

                <div>
                  <label
                    htmlFor="contactPersonName"
                    className="text-xbase block mb-1"
                  >
                    Contact Person Name{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    id="contactPersonName"
                    name="contactPersonName"
                    value={account.contactPersonName}
                    onChange={handleAccountInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        contactPersonName: !validator.check(
                          account.contactPersonName,
                          'required|min:2',
                        ),
                      }));
                    }}
                    error={errors.contactPersonName}
                  />
                  {validator.message(
                    'contactPersonName',
                    account.contactPersonName,
                    'required|min:2',
                    {
                      messages: {
                        required: 'Contact person name is required.',
                      },
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>

                <div className="flex flex-row gap-5">
                  <div className="flex-1 w-full">
                    <label htmlFor="email" className="text-xbase block mb-1">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      value={account.email}
                      type="email"
                      autoComplete="username"
                      onChange={handleAccountInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          email: !validator.check(account.email, 'email'),
                        }));
                      }}
                      error={errors.email}
                    />
                    {validator.message('email', account.email, 'email', {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>

                  <div className="w-[40%]">
                    <label
                      htmlFor="phoneNumber"
                      className="text-xbase block mb-1"
                    >
                      Phone
                    </label>
                    <PhoneNumberInput
                      id="phoneNumber"
                      name="phoneNumber"
                      defaultCountry={
                        account.countryCode
                          ? (account.countryCode as Country)
                          : 'GH'
                      }
                      value={account.phoneNumber}
                      onChange={value =>
                        setAccount(fndAccount => ({
                          ...fndAccount,
                          phoneNumber: value,
                        }))
                      }
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          phoneNumber: !validator.check(
                            account.phoneNumber,
                            'phone',
                          ),
                        }));
                      }}
                      error={errors.phoneNumber}
                    />
                    {validator.message(
                      'phoneNumber',
                      account.phoneNumber,
                      'phone',
                      {
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label htmlFor="password" className="text-xbase block mb-1">
                      Password
                    </label>
                    <Input
                      id="password"
                      name="password"
                      value={account.password}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          password: !validator.check(account.password, 'min:8'),
                        }));
                      }}
                      error={errors.password}
                    />
                    {validator.message('password', account.password, 'min:8', {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>

                  <div className="w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="text-xbase block mb-1"
                    >
                      Confirm Password
                    </label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      value={account.confirmPassword}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          confirmPassword: !validator.check(
                            account.confirmPassword,
                            'min:8',
                          ),
                        }));
                      }}
                      error={errors.confirmPassword}
                    />
                    {validator.message(
                      'confirmPassword',
                      account.confirmPassword,
                      'min:8',
                      {
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="border-b border-[#d0d7de]">
                  <h2 className="text-xbase font-semibold">
                    Notes & Preferences
                  </h2>
                </div>

                <div className="flex flex-row mt-6">
                  <TextBox
                    id="notes"
                    name="notes"
                    value={account.notes}
                    onChange={handleAccountInputChange}
                  />
                </div>

                {/* <div className="flex flex-row  mt-4 space-x-10">
                <div className="">
                  <label htmlFor="chargeTax" className="text-xbase block mb-1">
                    Taxable
                  </label>
                  <ToggleInput
                    id="chargeTax"
                    name="chargeTax"
                    value="chargeTax"
                    checked={chargeTax}
                    onChange={event => setChargeTax(event.target.checked)}
                    className="align-middle cursor-pointer"
                  />
                </div>
  
                <div className="">
                  <label htmlFor="chargeTax" className="text-xbase block mb-1">
                    Email Marketing
                  </label>
                  <ToggleInput
                    id="chargeTax"
                    name="chargeTax"
                    value="chargeTax"
                    checked={chargeTax}
                    onChange={event => setChargeTax(event.target.checked)}
                    className="align-middle cursor-pointer"
                  />
                </div>
              </div> */}

                <div className="flex flex-row  mt-4">
                  <h2 className="text-xbase font-semibold">
                    Last Login:{' '}
                    <span className="font-light text-pos-gray">
                      Thu, Aug 18, 2022 7:47 AM
                    </span>
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b 
        border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Address</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="address" className="text-xbase block mb-1">
                    Address
                  </label>
                  <Input
                    id="address"
                    name="address"
                    value={account.address}
                    onChange={handleAccountInputChange}
                  />
                </div>

                <div>
                  <label
                    htmlFor="otherAddress"
                    className="text-xbase block mb-1"
                  >
                    Other Address
                  </label>
                  <Input
                    id="otherAddress"
                    name="otherAddress"
                    value={account.otherAddress}
                    onChange={handleAccountInputChange}
                  />
                </div>

                <div className="flex flex-row gap-5">
                  <div className="flex-1">
                    <label
                      htmlFor="countryCode"
                      className="text-xbase block mb-1"
                    >
                      Country
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      value={account.countryCode}
                      options={countries.map(fndCountry => ({
                        value: fndCountry.iso,
                        label: fndCountry.name,
                      }))}
                      id="countryCode"
                      name="countryCode"
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="text-xbase block mb-1">
                      City
                    </label>
                    <Input
                      id="city"
                      name="city"
                      value={account.city}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="text-xbase block mb-1">
                      State / Region
                    </label>
                    <Input
                      id="state"
                      name="state"
                      value={account.state}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="postalCode" className="text-xbase block mb-1">
                    Postal code
                  </label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={account.postalCode}
                    onChange={handleAccountInputChange}
                    className="w-[250px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  },
);

export default SupplierForm;
